import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Wallet as WalletIcon, Plus, X } from 'lucide-react';
import { walletAPI } from '../../services/api';
import toast from 'react-hot-toast';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// Load the Cashfree v3 SDK once → resolves the global Cashfree factory.
let _cfPromise = null;
const loadCashfree = () => {
  if (window.Cashfree) return Promise.resolve(window.Cashfree);
  if (_cfPromise) return _cfPromise;
  _cfPromise = new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
    el.onload = () => resolve(window.Cashfree);
    el.onerror = () => reject(new Error('Failed to load payment SDK'));
    document.body.appendChild(el);
  });
  return _cfPromise;
};

// `dark` = the graphite sidebar skin (v1.238.0). The recharge modal is unchanged either way.
export default function WalletWidget({ collapsed, dark, onBilling }) {
  const [info, setInfo] = useState({ balance: 0, per_order_rate: 5, gst_rate: 18, recharge_min: 1000, presets: [1000, 2500, 5000] });
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    try { const r = await walletAPI.get(); setInfo(r.data); } catch {}
  }, []);

  useEffect(() => {
    load();
    // OrdersPage dispatches this after a ship/cancel so the balance stays fresh.
    const h = () => load();
    window.addEventListener('wallet:refresh', h);
    return () => window.removeEventListener('wallet:refresh', h);
  }, [load]);

  const low = info.balance < info.per_order_rate;

  if (collapsed) {
    return (
      <>
        <button style={{ ...s.collapsed, ...(dark ? s.collapsedDark : null), color: low ? (dark ? '#FF6961' : '#dc2626') : (dark ? '#30D158' : '#2563eb') }} title={`Wallet: ${inr(info.balance)}`} onClick={() => setShowModal(true)}>
          <WalletIcon size={18} />
        </button>
        {showModal && <RechargeModal info={info} onClose={() => setShowModal(false)} onDone={load} />}
      </>
    );
  }

  return (
    <>
      <div style={dark
        ? { ...s.card, ...s.cardDark, background: low ? 'rgba(255,69,58,0.12)' : 'rgba(255,255,255,0.05)' }
        : { ...s.card, borderColor: low ? '#fecaca' : '#e2e8f0', background: low ? '#fef2f2' : '#fff' }}>
        <div style={s.row}>
          <div style={{ ...s.label, ...(dark ? s.labelDark : null) }}><WalletIcon size={13} style={dark ? { color: low ? '#FF6961' : '#30D158' } : undefined} /> Wallet</div>
          <button style={{ ...s.rechargeBtn, ...(dark ? s.rechargeDark : null) }} onClick={() => setShowModal(true)}><Plus size={12} /> Recharge</button>
        </div>
        <div style={{ ...s.balance, ...(dark ? s.balanceDark : null), color: low ? (dark ? '#FF6961' : '#dc2626') : (dark ? '#F5F5F7' : '#0f172a') }}>{inr(info.balance)}</div>
        {low && <div style={{ ...s.lowNote, ...(dark ? { color: '#FF6961' } : null) }}>Low balance — recharge to ship</div>}
        {/* v1.239.0 - Billing left the menu; this is its door. */}
        {onBilling && <button style={{ ...s.billingLink, ...(dark ? { color: '#A1A1A6' } : null) }} onClick={onBilling}>View billing →</button>}
      </div>
      {showModal && <RechargeModal info={info} onClose={() => setShowModal(false)} onDone={load} />}
    </>
  );
}

function RechargeModal({ info, onClose, onDone }) {
  const [amount, setAmount] = useState(info.presets?.[0] || 1000);
  const [busy, setBusy] = useState(false);
  const amt = Math.max(0, Math.floor(Number(amount) || 0));
  const gst = Math.round(amt * (info.gst_rate || 18)) / 100;
  const total = amt + gst;
  const valid = amt >= (info.recharge_min || 1000);

  const pay = async () => {
    if (!valid) return toast.error(`Minimum recharge is ${inr(info.recharge_min)}`);
    setBusy(true);
    try {
      const r = await walletAPI.recharge(amt);
      // Gateway OFF → instant test-credit (unchanged).
      if (r.data.mode === 'demo') {
        toast.success(`Recharge successful — ${amt.toLocaleString('en-IN')} credits added`);
        window.dispatchEvent(new Event('wallet:refresh'));
        onDone && onDone();
        onClose();
        return;
      }
      // Gateway ON → redirect to Cashfree hosted checkout; returns to /dashboard/billing.
      const Cashfree = await loadCashfree();
      const cashfree = Cashfree({ mode: r.data.mode === 'production' ? 'production' : 'sandbox' });
      await cashfree.checkout({ paymentSessionId: r.data.payment_session_id, redirectTarget: '_self' });
      // Page redirects away; no further code runs.
    } catch (e) {
      toast.error(e.response?.data?.error || 'Recharge failed');
      setBusy(false);
    }
  };

  // Portal to <body> so the fixed overlay escapes the sidebar (position:sticky + overflow:hidden
  // trapped it in the sidebar box, rendering the modal clipped at bottom-left instead of centered).
  return createPortal(
    <div style={s.overlay} onClick={onClose}>
      <div style={s.modal} onClick={e => e.stopPropagation()}>
        <div style={s.modalHead}>
          <h3 style={s.modalTitle}>Recharge Wallet</h3>
          <button style={s.closeBtn} onClick={onClose}><X size={16} /></button>
        </div>
        <div style={s.chips}>
          {(info.presets || []).map(p => (
            <button key={p} onClick={() => setAmount(p)}
              style={{ ...s.chip, ...(amt === p ? s.chipActive : {}) }}>{inr(p)}</button>
          ))}
        </div>
        <label style={s.inLabel}>Amount (credits) — min {inr(info.recharge_min)}</label>
        {/* Credits are whole ₹1 units — accept digits only so the input never shows a fraction the total drops. */}
        <input type="text" inputMode="numeric" min={info.recharge_min} value={amount}
          onChange={e => { const v = e.target.value; if (/^\d*$/.test(v)) setAmount(v); }} style={s.input} />
        <div style={s.breakup}>
          <div style={s.bRow}><span>Recharge amount</span><b>{inr(amt)}</b></div>
          <div style={s.bRow}><span>GST @ {info.gst_rate}%</span><b>{inr(gst)}</b></div>
          <div style={{ ...s.bRow, ...s.bTotal }}><span>Total payable</span><b>{inr(total)}</b></div>
          <div style={s.creditNote}>You get <b>{amt.toLocaleString('en-IN')} credits</b> (1 credit = ₹1)</div>
        </div>
        <button style={{ ...s.payBtn, opacity: valid && !busy ? 1 : 0.6 }} disabled={!valid || busy} onClick={pay}>
          {busy ? 'Processing…' : `Pay ${inr(total)}`}
        </button>
        <div style={s.gwNote}>{info.gateway === 'cashfree'
          ? 'Secure payment via Cashfree. Credits are added once payment succeeds.'
          : 'Payment gateway not enabled — credits are added instantly for now.'}</div>
      </div>
    </div>,
    document.body
  );
}

const s = {
  card: { margin: '4px 8px 8px', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 12 },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  label: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: .5 },
  rechargeBtn: { display: 'flex', alignItems: 'center', gap: 3, background: '#2563eb', color: '#fff', border: 'none', borderRadius: 7, padding: '4px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer' },
  balance: { fontSize: 20, fontWeight: 800, marginTop: 6 },
  lowNote: { fontSize: 10, color: '#dc2626', marginTop: 2 },
  collapsed: { background: 'transparent', border: 'none', cursor: 'pointer', padding: 8, display: 'flex', justifyContent: 'center' },
  // graphite sidebar skin
  billingLink: { display: 'block', background: 'none', border: 'none', padding: 0, marginTop: 6, fontSize: 11.5, fontWeight: 500, color: '#2563eb', cursor: 'pointer', fontFamily: 'inherit' },
  collapsedDark: { width: 'calc(100% - 20px)', margin: '4px 10px 8px', height: 40, alignItems: 'center', borderRadius: 10, background: 'rgba(255,255,255,0.05)' },
  cardDark: { margin: '4px 10px 8px', border: 'none', borderRadius: 12, whiteSpace: 'nowrap', overflow: 'hidden' },
  labelDark: { color: '#8E8E93', textTransform: 'none', letterSpacing: 0, fontWeight: 500, fontSize: 12 },
  rechargeDark: { background: 'rgba(255,255,255,0.12)', color: '#FFFFFF', borderRadius: 7 },
  balanceDark: { fontWeight: 700, fontSize: 18, letterSpacing: -0.3 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 },
  modal: { background: '#fff', borderRadius: 16, padding: 22, width: 380, maxWidth: '92vw', boxShadow: 'var(--mc-e4)' },
  modalHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 },
  modalTitle: { margin: 0, fontSize: 17, fontWeight: 700, color: '#0f172a' },
  closeBtn: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b' },
  chips: { display: 'flex', gap: 8, marginBottom: 12 },
  chip: { flex: 1, padding: '9px 0', border: '1px solid #e2e8f0', borderRadius: 9, background: '#fff', cursor: 'pointer', fontWeight: 600, color: '#334155', fontSize: 13 },
  chipActive: { borderColor: '#2563eb', background: '#eff6ff', color: '#2563eb' },
  inLabel: { fontSize: 11, color: '#64748b', fontWeight: 600 },
  input: { width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 9, fontSize: 15, marginTop: 4, boxSizing: 'border-box' },
  breakup: { marginTop: 14, padding: 12, background: '#f8fafc', borderRadius: 10 },
  bRow: { display: 'flex', justifyContent: 'space-between', fontSize: 13, color: '#475569', padding: '3px 0' },
  bTotal: { borderTop: '1px solid #e2e8f0', marginTop: 4, paddingTop: 7, fontSize: 15, color: '#0f172a' },
  creditNote: { fontSize: 11, color: '#16a34a', marginTop: 6, textAlign: 'center' },
  payBtn: { width: '100%', marginTop: 16, padding: '12px 0', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  gwNote: { fontSize: 10, color: '#94a3b8', textAlign: 'center', marginTop: 8 },
};
